/**
 * React Query hooks for authentication
 * Provides reactive authentication state management
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authClient } from "../clients/auth-client";

// Global retry counter to prevent infinite loops
let globalRetryCount = 0;
const MAX_GLOBAL_RETRIES = 3;

// Reset retry counter when successful
const resetGlobalRetryCount = () => {
  globalRetryCount = 0;
};

// Check if we should stop retrying globally
const shouldStopRetrying = () => {
  return globalRetryCount >= MAX_GLOBAL_RETRIES;
};

/**
 * Hook to fetch and manage access token
 */
export function useAccessToken() {
  return useQuery({
    queryKey: ["accessToken"],
    queryFn: async () => {
      // Stop retrying if we've exceeded global limit
      if (shouldStopRetrying()) {
        throw new Error(
          "Authentication failed after maximum retries. Please check your credentials."
        );
      }

      try {
        const token = await authClient.getAccessToken();
        resetGlobalRetryCount(); // Reset on success
        return {
          token,
          isExpired: authClient.isTokenExpired(),
        };
      } catch (error) {
        globalRetryCount++;
        throw error;
      }
    },
    refetchInterval: (data) => {
      // Stop refetching if we've exceeded global retry limit
      if (shouldStopRetrying()) {
        return false; // Stop refetching
      }

      // Refetch 5 minutes before expiry
      if (data && "isExpired" in data && data.isExpired) return 1000; // Refetch immediately if expired
      return 50 * 60 * 1000; // Check every 50 minutes
    },
    staleTime: 50 * 60 * 1000, // Consider data stale after 50 minutes
    retry: 3, // Maximum 3 retries per query
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff, max 30s
    retryOnMount: false, // Don't retry on mount if it failed before
    enabled: !shouldStopRetrying(), // Disable query if we've exceeded global retries
  });
}

/**
 * Hook to manually set access token
 */
export function useSetAccessToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      expiresIn,
    }: {
      token: string;
      expiresIn?: number;
    }) => {
      authClient.setAccessToken(token, expiresIn);
      resetGlobalRetryCount(); // Reset on manual set
      return { token, isExpired: false };
    },
    onSuccess: (data) => {
      // Update the cache
      queryClient.setQueryData(["accessToken"], data);
    },
  });
}

/**
 * Hook to refresh access token
 */
export function useRefreshToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Stop retrying if we've exceeded global limit
      if (shouldStopRetrying()) {
        throw new Error(
          "Authentication failed after maximum retries. Please check your credentials."
        );
      }

      try {
        const tokenInfo = await authClient.fetchAccessToken();
        resetGlobalRetryCount(); // Reset on success
        return {
          token: tokenInfo.access_token,
          isExpired: false,
        };
      } catch (error) {
        globalRetryCount++;
        throw error;
      }
    },
    onSuccess: (data) => {
      // Update the cache
      queryClient.setQueryData(["accessToken"], data);
    },
    retry: 3, // Maximum 3 retries per mutation
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff, max 30s
  });
}

/**
 * Hook to clear authentication
 */
export function useClearAuth() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      authClient.clearAuthentication();
      resetGlobalRetryCount(); // Reset when clearing auth
    },
    onSuccess: () => {
      // Clear the cache
      queryClient.removeQueries({ queryKey: ["accessToken"] });
    },
  });
}

/**
 * Hook to reset retry counter (useful for manual recovery)
 */
export function useResetRetryCount() {
  return () => {
    resetGlobalRetryCount();
  };
}
